import type { Route } from "../../../utils/models/Route";
import { cumulativeDistances, MeasuredLine } from "../utils/trailLine";
import { nearestAlongLine } from "./day-flags";
import { haversineMeters } from "./stage-connectors";
import { DESTINATION_ATTACHED_THRESHOLD_M } from "./stage-destinations";
import { ENDPOINT_ACCOMMODATION_TYPES, NEARBY_BUFFER_M, NearbyPlace, placeMatchesQuery } from "./route-endpoints";
import { HikeHighlightPayload, poiToHikeHighlight } from "../../../utils/helpers/PoiToHikeHighlightHelper";

/**
 * The highlights listed on each route card, and the places offered when
 * searching for more (RouteHighlights.vue, HighlightSearchModal.vue).
 */

export interface HighlightLike {
  clientId: string;
  title?: string;
  coordinates?: number[];
  dayNumber?: number | null;
  poiType?: string;
  poiId?: string | number;
}

export interface RouteHighlight<H> {
  highlight: H;
  /** How far along the route it is, in metres. */
  alongM: number;
  /** How far off the route it is, in metres. */
  offRouteM: number;
}

/** A route's line as one measured run of points — null without one. */
export function measuredRouteLine(route: Route | null | undefined): MeasuredLine | null {
  const coordinates = (route?.lineStrings ?? [])
    .flatMap((lineString: any) => lineString?.lineStringGeometry?.coordinates ?? []);
  if (coordinates.length < 2) return null;
  return { coordinates, cumDist: cumulativeDistances(coordinates) };
}

/**
 * Each route's highlights, in the order you'd walk past them. A highlight is
 * listed once, on the route of its own day it's nearest to (any route, when
 * its day has none). Transport legs don't list highlights.
 */
export function highlightsByRoute<H extends HighlightLike>(
  routes: { [routeKey: string]: Route },
  highlights: H[]
): { [routeKey: string]: RouteHighlight<H>[] } {
  const lines = Object.entries(routes)
    .filter(([, route]) => (route.route_style ?? 'route') !== 'transport')
    .map(([routeKey, route]) => ({ routeKey, day: route.day ?? 1, line: measuredRouteLine(route) }))
    .filter((entry): entry is { routeKey: string; day: number; line: MeasuredLine } => entry.line !== null);

  const byRoute: { [routeKey: string]: RouteHighlight<H>[] } = {};
  highlights.forEach(highlight => {
    const coord = highlight.coordinates;
    if (!coord || coord.length < 2) return;
    const ownDay = lines.filter(entry => entry.day === (highlight.dayNumber ?? null));
    const candidates = ownDay.length ? ownDay : lines;
    let best: { routeKey: string; alongM: number; offRouteM: number } | null = null;
    candidates.forEach(entry => {
      const nearest = nearestAlongLine(entry.line, coord);
      if (!best || nearest.offLineM < best.offRouteM) {
        best = { routeKey: entry.routeKey, alongM: nearest.distanceM, offRouteM: nearest.offLineM };
      }
    });
    if (!best) return;
    const { routeKey, alongM, offRouteM } = best;
    (byRoute[routeKey] ??= []).push({ highlight, alongM, offRouteM });
  });
  Object.values(byRoute).forEach(list => list.sort((a, b) => a.alongM - b.alongM));
  return byRoute;
}

/**
 * A day's routes as one measured run of points, in the order they're walked
 * (their position in the plan) — what its highlights are ordered along and
 * searched near. Transport legs aren't walked; null for a day with no line.
 */
export function dayLine(routes: { [routeKey: string]: Route }, dayNumber: number): MeasuredLine | null {
  const coordinates = Object.values(routes)
    .filter(route => (route.day ?? 1) === dayNumber && (route.route_style ?? 'route') !== 'transport')
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .flatMap(route => (route.lineStrings ?? []).flatMap((lineString: any) => lineString?.lineStringGeometry?.coordinates ?? []));
  if (coordinates.length < 2) return null;
  return { coordinates, cumDist: cumulativeDistances(coordinates) };
}

/**
 * Each day's highlights, in the order you'd walk past them — a highlight
 * belongs to the day of the route it's listed on (see highlightsByRoute).
 */
export function highlightsByDay<H extends HighlightLike>(
  routes: { [routeKey: string]: Route },
  highlights: H[]
): { [dayNumber: number]: RouteHighlight<H>[] } {
  const byDay: { [dayNumber: number]: RouteHighlight<H>[] } = {};
  Object.entries(highlightsByRoute(routes, highlights)).forEach(([routeKey, items]) => {
    const day = routes[routeKey]?.day ?? 1;
    (byDay[day] ??= []).push(...items);
  });
  Object.entries(byDay).forEach(([day, items]) => {
    const line = dayLine(routes, Number(day));
    if (!line) return;
    byDay[Number(day)] = items
      .map(item => {
        const nearest = nearestAlongLine(line, item.highlight.coordinates!);
        return { highlight: item.highlight, alongM: nearest.distanceM, offRouteM: nearest.offLineM };
      })
      .sort((a, b) => a.alongM - b.alongM);
  });
  return byDay;
}

/** A list folded to its first `shown` items unless expanded, and how many are tucked away. */
export function foldedList<T>(items: T[], expanded: boolean, shown: number = 1): { visible: T[]; hiddenCount: number } {
  if (expanded || items.length <= shown) return { visible: items, hiddenCount: 0 };
  return { visible: items.slice(0, shown), hiddenCount: items.length - shown };
}

/** How far either side of a route places are offered as highlights — as far as for its Start or End. */
export const HIGHLIGHT_SEARCH_CORRIDOR_M = NEARBY_BUFFER_M;

/**
 * How far either side places are found when searched for by name — someone
 * who names a place knows it's there, a village off the route, say.
 */
export const HIGHLIGHT_NAME_SEARCH_M = 5000;

export type HighlightPlaceGroup = 'all' | 'sights' | 'food' | 'stay' | 'practical';

/**
 * The amenity types in each group of the highlight search — between them,
 * every type we keep (Amenity::TYPES), so anything on the map can be found;
 * places to stay add every accommodation.
 */
export const HIGHLIGHT_AMENITY_GROUPS: { [group in Exclude<HighlightPlaceGroup, 'all'>]: string[] } = {
  sights: ['viewpoint', 'peak', 'waterfall', 'history', 'cave', 'wildlife', 'swimming', 'picnic', 'playground'],
  food: ['pub', 'restaurant', 'coffee', 'fastfood', 'shop'],
  stay: ['osmcampsite', 'shelter'],
  practical: [
    'water', 'restroom', 'information', 'town', 'transport_station', 'bus_stop', 'pharmacy', 'post_office',
    'healthcare', 'warning', 'parking', 'ev_charging', 'wifi', 'storage', 'gate', 'junction', 'waypoint',
  ],
};

export const HIGHLIGHT_ACCOMMODATION_TYPES = ENDPOINT_ACCOMMODATION_TYPES;
export const HIGHLIGHT_AMENITY_TYPES = Object.values(HIGHLIGHT_AMENITY_GROUPS).flat();

function inGroup(place: NearbyPlace, group: HighlightPlaceGroup): boolean {
  if (group === 'all') return true;
  if (group === 'stay' && place.category === 'Accommodation') return true;
  return place.category === 'Amenity' && HIGHLIGHT_AMENITY_GROUPS[group].includes(place.type);
}

/** Places closer than this with the same name are the same place mapped twice. */
const SAME_PLACE_M = 50;

/** Each place once, where the map has it twice (same name, practically the same spot). */
function withoutDuplicates(places: NearbyPlace[]): NearbyPlace[] {
  const kept: NearbyPlace[] = [];
  places.forEach(place => {
    const name = place.name.trim().toLowerCase();
    const duplicate = kept.some(other =>
      other.name.trim().toLowerCase() === name && haversineMeters(other.coordinates, place.coordinates) < SAME_PLACE_M);
    if (!duplicate) kept.push(place);
  });
  return kept;
}

export interface HighlightCandidate extends RouteHighlight<NearbyPlace> {
  /** Already one of the plan's highlights. */
  added: boolean;
}

/** Whether the plan already has a highlight for `place` — linked to it, or the same name in the same spot. */
export function isAlreadyHighlighted(place: NearbyPlace, highlights: HighlightLike[]): boolean {
  const name = place.name.trim().toLowerCase();
  return highlights.some(highlight =>
    (highlight.poiType === place.category && String(highlight.poiId) === String(place.id)) ||
    (!!highlight.coordinates &&
      (highlight.title ?? '').trim().toLowerCase() === name &&
      haversineMeters(highlight.coordinates, place.coordinates) <= DESTINATION_ATTACHED_THRESHOLD_M));
}

/**
 * The places to offer as highlights for a route: those within `corridorM`
 * of its line — by default HIGHLIGHT_SEARCH_CORRIDOR_M, or the wider
 * HIGHLIGHT_NAME_SEARCH_M once a name's typed — in the chosen group and
 * matching `query`, in the order you'd walk past them.
 */
export function highlightCandidates(
  places: NearbyPlace[],
  line: MeasuredLine,
  options: { query?: string; group?: HighlightPlaceGroup; highlights?: HighlightLike[]; corridorM?: number } = {}
): HighlightCandidate[] {
  const { query = '', group = 'all', highlights = [] } = options;
  const corridorM = options.corridorM ?? (query.trim() ? HIGHLIGHT_NAME_SEARCH_M : HIGHLIGHT_SEARCH_CORRIDOR_M);
  return withoutDuplicates(places)
    .filter(place => inGroup(place, group) && placeMatchesQuery(place, query))
    .map(place => {
      const nearest = nearestAlongLine(line, place.coordinates);
      return { highlight: place, alongM: nearest.distanceM, offRouteM: nearest.offLineM, added: isAlreadyHighlighted(place, highlights) };
    })
    .filter(candidate => candidate.offRouteM <= corridorM)
    .sort((a, b) => a.alongM - b.alongM);
}

/** The highlight to add for a place found near a route, on that route's day — linked back to the place. */
export function placeHighlight(place: NearbyPlace, dayNumber: number): HikeHighlightPayload & { dayNumber: number } {
  const [lng, lat] = place.coordinates;
  return {
    ...poiToHikeHighlight({ type: place.type, name: place.name, lng, lat, poiClass: place.category, poiId: place.id }),
    dayNumber,
  };
}
